import { Component, EventEmitter, Input, Output } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { Subscription, debounceTime, filter, retry } from 'rxjs'
import { SharedService } from 'src/app/shared/services/shared.service'
import { TranslateService } from '@ngx-translate/core'
import { ActivatedRoute, Router } from '@angular/router'
import { StoreService } from '../../services/store.service'
import { PaymentFilterOrderEnum, PaymentStatus } from '../../models/order.enum'

@Component({
	selector: 'app-orders-filter',
	templateUrl: './orders-filter.component.html',
	styleUrl: './orders-filter.component.scss',
})
export class OrdersFilterComponent {
	@Output() emitFilter = new EventEmitter()
	@Output() emitFilterCount = new EventEmitter()
	@Input() filterData: any = {
		paging: { pageNumber: 1, pageSize: 10 },
		searchText: '',
		startDate: '',
		endDate: '',
		status: '',
		countryId: null,
		paymentStatus: null,
		paymentMethod: null,
		cityId: null,
		areaId: null,
		orderStatus: 0,
	}

	filterForm = new FormGroup({
		startDate: new FormControl(null),
		endDate: new FormControl(null),
		paymentStatus: new FormControl(null),
		paymentMethod: new FormControl(null),
		cityId: new FormControl(null),
		areaId: new FormControl(null),
	})

	subscription = new Subscription()
	filterCount = 0
	paymentArray = this.sharedServ.returnEnumArray(PaymentFilterOrderEnum)
	PaymentStatus = this.sharedServ.returnEnumArray(PaymentStatus)
	showAreaList = false
	showCityList = false
	cityValue: any
	keywordCity = new FormControl('')
	cities: any = []
	Areas: any = []
	cityListFilter: any = {
		searchText: '',
		country: 0,
	}
	areaValue: any
	keywordArea = new FormControl('')

	areaListFilter: any = {
		paging: { pageNumber: 1, pageSize: 10 },
		searchText: '',
		cityId: null,
	}
	country: any
	isCitiesLoaded = false
	isAreasLoaded = false

	constructor(
		private sharedServ: SharedService,
		public translate: TranslateService,
		private ActivatedRoute: ActivatedRoute,
		private orderService: StoreService,
		private router: Router
	) {}

	ngOnInit(): void {
		this.searchOnSelect(this.keywordArea)
		this.searchOnSelect(this.keywordCity)
		this.loadFilterState()
		this.countSubscription()

		this.filterForm.controls['cityId'].valueChanges.subscribe(() => {
			this.clearAreaSelection()
			this.getAreas()
		})
	}

	countSubscription() {
		this.filterForm.valueChanges.subscribe((val) => {
			this.filterCount = 0
			for (let value in val) {
				if (
					(this.getController(value).value ||
						this.getController(value).value == 0) &&
					value !== 'paymentStatus' &&
					value !== 'paymentMethod'
				) {
					this.filterCount += 1
				} else if (
					(value == 'paymentStatus' || value == 'paymentMethod') &&
					this.getController(value).value?.length
				) {
					this.filterCount += 1
				}
			}
			this.saveFilterState() // Save state whenever filterCount changes
		})
	}
	ngOnChanges() {
		this.getAreas()
		this.countSubscription()
		this.loadFilterState()
	}

	submit() {
		this.filterData = {
			...this.filterData,
			...this.filterForm.getRawValue(),
		}
		delete this.filterData.searchText
		delete this.filterData.orderStatus
		this.emitFilter.emit(this.filterData)

		this.emitFilterCount.emit(this.filterCount)
		this.updateUrlParams()
	}

	resetFilter() {
		this.filterForm.reset()

		this.cities = []
		this.Areas = []
		this.showCityList = false
		this.showCityList = false
		this.keywordCity.setValue('')
		this.keywordArea.setValue(null)
		this.isCitiesLoaded = false
		this.isAreasLoaded = false
		this.filterCount = 0
		this.filterData = {
			...this.filterData,
			...this.filterForm.getRawValue(),
		}
		delete this.filterData.orderStatus
		this.emitFilter.emit(this.filterData)
		this.emitFilterCount.emit(this.filterCount)
		this.getCities()
		this.getAreas()
		this.updateUrlParams()
		localStorage.removeItem('orderFilter')
	}

	searchOnSelect(keyWordSearch: any) {
		keyWordSearch.valueChanges
			.pipe(
				filter((q: any) => q?.trim().length > 1 || !q?.trim().length),
				debounceTime(700),
				retry(-1)
			)
			.subscribe((res: any) => {
				keyWordSearch == this.keywordCity ? this.getCities() : this.getAreas()
			})
	}

	getCities() {
		if (this.isCitiesLoaded) return

		this.country = this.ActivatedRoute.snapshot.queryParams['country']
		this.cityListFilter.searchText = this.keywordCity.value
		this.cityListFilter.country = this.country

		this.subscription.add(
			this.orderService.getAllCities(this.cityListFilter).subscribe({
				next: (res: any) => {
					if (res) {
						this.cities = res.result
						this.restoreCitySelection()
						this.isCitiesLoaded = true
					}
				},
			})
		)
	}

	getAreas() {
		if (!this.isAreasLoaded) {
			if (this.filterForm.controls['cityId'].value) {
				this.areaListFilter.cityId = this.getController('cityId').value
				this.areaListFilter.searchText = this.keywordArea?.value

				this.subscription.add(
					this.orderService
						.getAllAreaies(this.areaListFilter)
						.subscribe((res: any) => {
							this.Areas = res?.result
							this.restoreAreaSelection()
							this.isAreasLoaded = true
						})
				)
			}
		}
	}

	restoreCitySelection() {
		const selectedCity = this.cities.find(
			(city: any) => city.id === this.filterData.cityId
		)
		if (selectedCity) {
			this.keywordCity.setValue(
				this.translate.currentLang === 'en'
					? selectedCity?.name?.en
					: selectedCity?.name?.ar
			)
			this.getAreas() // Ensure areas are fetched for the selected city
		}
	}

	restoreAreaSelection() {
		const selectedArea = this.Areas.find(
			(area: any) => area.id === this.filterData.areaId
		)
		if (selectedArea) {
			this.keywordArea.setValue(
				this.translate.currentLang === 'en'
					? selectedArea?.name?.en
					: selectedArea?.name?.ar
			)
		}
	}

	clearAreaSelection() {
		this.filterForm.controls['areaId'].setValue(null)
		this.keywordArea.setValue('')
		this.Areas = []
	}

	compareWith(a: any, b: any) {
		return a == b
	}

	paymentChanged(event: any, controller: string) {
		if (event?.checked) {
			!this.getController(controller).value
				? this.getController(controller).setValue([])
				: null
			this.getController(controller).setValue([
				...this.getController(controller).value,
				event.source.value,
			])
		} else {
			let objIndex = [...this.getController(controller).value]?.indexOf(
				event.source.value
			)
			let newArray = this.getController(controller).value
			newArray.splice(objIndex, 1)
			this.getController(controller).setValue(newArray)
		}
	}

	updateUrlParams() {
		if (this.filterCount > 0) {
			this.router.navigate(['/layout/store/orders'], {
				queryParams: { filterCount: this.filterCount },
				queryParamsHandling: 'merge', // Merge with existing query params
			})
		} else {
			this.router.navigate(['/layout/store/orders'], {
				queryParams: { filterCount: null },
				queryParamsHandling: 'merge', // Merge with existing query params
			})
		}
	}

	loadFilterState() {
		this.countSubscription()
		const savedFilterData = localStorage.getItem('orderFilter')
		if (savedFilterData) {
			const filterState = JSON.parse(savedFilterData)
			this.filterData = { ...filterState }
			this.filterForm.patchValue({
				cityId: this.filterData.cityId,
				areaId: this.filterData.areaId,
				paymentStatus: this.filterData.paymentStatus,
				paymentMethod: this.filterData.paymentMethod,
				startDate: this.filterData.startDate,
				endDate: this.filterData.endDate,
			})
			this.filterCount = filterState.filterCount

			this.emitFilterCount.emit(filterState.filterCount)

			this.getCities() // Ensure cities are fetched first
		}
	}

	saveFilterState() {
		this.filterData = {
			...this.filterData,
			...this.filterForm.getRawValue(),
			filterCount: this.filterCount,
		}
		localStorage.setItem('orderFilter', JSON.stringify(this.filterData))
	}

	getController(controlName: string) {
		return (this.filterForm as FormGroup).controls[controlName]
	}

	ngOnDestroy() {
		this.subscription.unsubscribe()
	}
}
